import { DynamicDrawUsage, InstancedMesh, Matrix4, Vector3 } from 'three';
import cart from '../../data/cart.generated.json';
import type { WorldFeature } from '../types';
import { SURFACE } from '../mesh/attributes';
import { createCitizenBatch } from './citizens/citizenBatch';
import { citizenGeometry } from './citizens/citizenShapes';
import { clipPoseAt, walkPose } from './citizens/citizenPose';
import { cargoGround } from './cargoGround';
import { alongLine, lineOf, type OnLine } from './crowd/walkLine';
import { CARGO, cargoHeadingAt, cargoX, planCargoRoutes } from './cargoPlan';
import { cargoMotionAt, cargoStyle } from './cargoMotion';
import { streetAssetGeometry, streetInstances } from './streetAsset';

export const cargoFeature: WorldFeature = {
  id: 'moving-cargo',
  label: 'Cart workers and moving cargo',
  weight: 0.5,
  build(ctx) {
    const asset = ctx.citizens,
      crowd = ctx.lifePlan?.crowd;
    if (!asset || !crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const routes =
      ctx.lifePlan?.cargoRoutes ??
      planCargoRoutes(crowd, cargoGround(ctx.layout, ctx.site, ctx.lots));
    const figures = asset.figures.filter((f) => f.trade === 'carter');
    if (!routes.length || !figures.length)
      return { objects: [], animated: [], obstacles: [], counts: {} };
    ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
    const batch = createCitizenBatch(
      ctx.materials.painted(),
      figures.map((f) => citizenGeometry(asset, f.lods[0]!)),
      routes.length,
      'cart-pushing-workers'
    );
    const material = ctx.materials.painted({ instanced: true });
    const parts = [cart.body, cart.wheel, cart.peat, cart.timber, cart.sacks, cart.barrels];
    const meshes = parts.map((part, k) => {
      const count = k === 1 ? routes.length * 2 : routes.length,
        geometry = streetAssetGeometry(part, SURFACE.wood);
      streetInstances(geometry, count, 1920);
      const mesh = new InstancedMesh(geometry, material, count);
      mesh.name = [
        'handcart-bodies',
        'handcart-wheels',
        'cart-peat',
        'cart-timber',
        'cart-sacks',
        'cart-barrels',
      ][k]!;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      return mesh;
    });
    const lines = routes.map((route) =>
      lineOf(route.map(([x, y]) => [x, y, ctx.site.queries.groundHeightAt([x, y])]))
    );
    const styles = routes.map((_, i) => cargoStyle(i));
    const base = new Matrix4(),
      local = new Matrix4(),
      scale = new Vector3(),
      at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
    let day = true;
    const animated = {
      id: 'cargo-delivery-carts',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        batch.begin();
        lines.forEach((line, i) => {
          const style = styles[i]!;
          const motion = cargoMotionAt(line.length, style, seconds);
          const { distance } = motion;
          alongLine(
            line,
            (((distance % line.length) + line.length) % line.length) / line.length,
            at
          );
          const heading = cargoHeadingAt(line, distance);
          base
            .makeRotationY(heading)
            .scale(scale.setScalar(day ? 1 : 0))
            .setPosition(at.x, at.ground, -at.y);
          if (day) {
            const amount = motion.speed / style.speed;
            batch.draw(
              i % figures.length,
              base,
              clipPoseAt(asset.clips['IdleCart']!, seconds + i),
              walkPose(asset.clips['PushCart']!, distance, 1),
              amount * amount * (3 - 2 * amount)
            );
          }
          local.makeScale(style.length, 1, 1).setPosition(CARGO.gripX * (1 - style.length), 0, 0);
          meshes[0]!.setMatrixAt(i, local.premultiply(base));
          const axle = cargoX(CARGO.axleX, style.length);
          for (const side of [-1, 1]) {
            local
              .makeRotationZ(-distance / CARGO.wheelRadius)
              .setPosition(axle, CARGO.wheelRadius, side * 0.52);
            meshes[1]!.setMatrixAt(i * 2 + (side + 1) / 2, local.premultiply(base));
          }
          for (let load = 0; load < 4; load++) {
            local.makeTranslation(axle - CARGO.axleX, 0, 0).premultiply(base);
            if (style.load !== load) local.scale(scale.setScalar(0));
            meshes[load + 2]!.setMatrixAt(i, local);
          }
        });
        for (const mesh of meshes) mesh.instanceMatrix.needsUpdate = true;
        batch.end();
      },
    };
    animated.update(0);
    return {
      objects: [batch.mesh, ...meshes],
      animated: [animated],
      obstacles: [],
      counts: { cargoCarts: routes.length, cartWorkers: routes.length, cargoLoads: routes.length },
    };
  },
};
